import { useQuery } from '@apollo/client';
import Grid from '@material-ui/core/Grid';
import gql from 'graphql-tag';
import React, { useState } from 'react';
import LoadComponent from '../components/LoadComponent';
import TelegramSearch from './Dashboard/TelegramSearch';
import Status from './Dashboard/Status';
import DashboardItem from './Dashboard/DashboardItem';
import { Divider } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import Card from "./Dashboard/Card";

export const REPORTED_STATUS = {
	NO_ERROR: 0,
	F0: 1,
	F1: 10,
	F2: 20,
	F3A: 31,
	F3B: 32,
	F3C: 33,
	F4: 40,
	F5: 50,
	F6: 60,
	F7: 70,
	F8: 80,
	F9: 90,
	F10: 100,
	F11: 110
};

export const GATEWAY_STATUS = {
	F0A: 1,//STATUS_NO_CONNECTION
	F0B: 2,//STATUS_PERMANENT_ISSUE //Battery problems
};

// Dashboard layout as data. The status lists here are the single source of
// truth for both what each item renders and which statuses the counts query asks
// for, so a new item cannot end up without a count.
const COLUMNS = [
	{
		titleKey: "dashboard:Device Reception",
		items: [
			{ code: 'F0', titleKey: "dashboard:Gateway Error", gatewayStatuses: [GATEWAY_STATUS.F0A, GATEWAY_STATUS.F0B] },
			{ code: 'F1', titleKey: "dashboard:Never received a telegram", reportedStatuses: [REPORTED_STATUS.F1] },
			{ code: 'F2', titleKey: "dashboard:No Telegram Received longer than 18M - Dispo", reportedStatuses: [REPORTED_STATUS.F2] },
			{ code: 'F3', titleKey: "dashboard:No Telegram Received", reportedStatuses: [REPORTED_STATUS.F3A, REPORTED_STATUS.F3B, REPORTED_STATUS.F3C] },
			{ code: 'F5', titleKey: "dashboard:Measurement for Billing End Period missing", reportedStatuses: [REPORTED_STATUS.F5] },
		],
	},
	{
		titleKey: "dashboard:Telegram Processing",
		items: [
			{ code: 'F7', titleKey: "dashboard:Duplicate serial number entered - Dispo", reportedStatuses: [REPORTED_STATUS.F7] },
			{ code: 'F4', titleKey: "dashboard:Telegram Decryption Problem", reportedStatuses: [REPORTED_STATUS.F4] },
			{ code: 'F10', titleKey: "dashboard:No telegram received - input error device master", reportedStatuses: [REPORTED_STATUS.F10] },
			{ code: 'F11', titleKey: "dashboard:Telegram could not be processed - Please contact support", reportedStatuses: [REPORTED_STATUS.F11] },
		],
	},
	{
		titleKey: "dashboard:Device Errors",
		items: [
			{ code: 'F6', titleKey: "dashboard:Declining values measured - Dispo", reportedStatuses: [REPORTED_STATUS.F6] },
			{ code: 'F8', titleKey: "dashboard:Device error - Dispo", reportedStatuses: [REPORTED_STATUS.F8] },
		],
	},
];

const ALL_ITEMS = COLUMNS.flatMap(({ items }) => items);
const ALL_REPORTED_STATUSES = ALL_ITEMS.flatMap(({ reportedStatuses }) => reportedStatuses ?? []);
const ALL_GATEWAY_STATUSES = ALL_ITEMS.flatMap(({ gatewayStatuses }) => gatewayStatuses ?? []);

const GET_ORGANIZATION_STATS = gql`
    query getDashboardStats {
        organizationStats {
            gateways_count,
            sensors_count
            wireless_sensors_count
            buildings_count
            apartments_count
            building_coverage_count
        }
    }
`;

const GET_STATUS_COUNTS = gql`
    query getDashboardStatusCounts($reportedStatuses: [Int], $gatewayStatuses: [Int]) {
        reportedStatusCounts(reportedStatuses: $reportedStatuses) {
            status
            count
        }
        gatewayStatusCounts(gatewayStatuses: $gatewayStatuses) {
            status
            count
        }
    }
`;

const indexByStatus = (rows) => (rows ?? []).reduce((acc, { status, count }) => {
	acc[status] = count;
	return acc;
}, {});

export default function Dashboard() {
	const { data, error } = useQuery(GET_ORGANIZATION_STATS);
	const { data: countsData, loading: countsLoading, error: countsError } = useQuery(GET_STATUS_COUNTS, {
		variables: {
			reportedStatuses: ALL_REPORTED_STATUSES,
			gatewayStatuses: ALL_GATEWAY_STATUSES,
		},
	});
	const { t } = useTranslation(["dashboard"]);
	const [isDownloadingGlobal, setIsDownloadingGlobal] = useState(false);

	if (error) {
		return <div>Error</div>;
	}
	if (countsError) {
		console.error("Error fetching status counts:", countsError);
	}
	const organizationStats = data?.organizationStats ?? {};

	// status absent from the grouped result = no matching devices
	const reportedCounts = indexByStatus(countsData?.reportedStatusCounts);
	const gatewayCounts = indexByStatus(countsData?.gatewayStatusCounts);

	const countFor = ({ reportedStatuses, gatewayStatuses }) => {
		if (!countsData) {
			return undefined;
		}
		const [statuses, counts] = gatewayStatuses
			? [gatewayStatuses, gatewayCounts]
			: [reportedStatuses, reportedCounts];

		return statuses.reduce((sum, status) => sum + (counts[status] ?? 0), 0);
	};

	return (
		<LoadComponent>
			<Grid container spacing={3}>
				<Status data={organizationStats} />
				<TelegramSearch />
				{COLUMNS.map((column) => (
					<Grid item xs={12} md={4} key={column.titleKey}>
						<Card title={t(column.titleKey)}>
							{column.items.map((item, index) => (
								<React.Fragment key={item.code}>
									{index > 0 && <Divider />}
									<DashboardItem
										title={`${item.code}: ${t(item.titleKey)}`}
										reportedStatuses={item.reportedStatuses}
										gatewayStatuses={item.gatewayStatuses}
										count={countFor(item)}
										loading={countsLoading}
										isDownloadingGlobal={isDownloadingGlobal}
										setIsDownloadingGlobal={setIsDownloadingGlobal}
									/>
								</React.Fragment>
							))}
						</Card>
					</Grid>
				))}
			</Grid >
		</LoadComponent >
	);
}
